import { useEffect, useState } from 'react'

import InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import Product from '@src/quince-site-common/src/domains/Product'
import productService from '@src/quince-site-common/src/services/productService'
import { captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'

import { InfluencerCardProductDetails } from '../atoms/influencerCard/InfluencerCard'
import { getInitialSelectedOptions, isNestedProduct } from '../molecules/productCard/productCard.helpers'

const FATAL_EXCEPTION_MESSAGE = 'Failed to fetch products in InfluencerSection section'

function useInfluencerCardProducts(influencerDetailList: InfluencerDetails[], isB2BSelfService?: boolean) {
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true)
  const [products, setProducts] = useState<Product[]>()
  const [spidToProductMap, setSpidToProductMap] = useState<Record<number, InfluencerCardProductDetails>>({})
  const [contentfulProductIdToProductMap, setContentfulProductIdToProductMap] = useState<
    Record<string, InfluencerCardProductDetails>
  >({})

  useEffect(() => {
    void (async () => {
      const spids = !isB2BSelfService
        ? influencerDetailList.map(influencerDetail => influencerDetail.spid).filter(id => id != null)
        : undefined
      const contentfulProductIds = isB2BSelfService
        ? influencerDetailList.map(influencerDetail => influencerDetail.contentfulProductId).filter(id => id != null)
        : undefined

      try {
        setIsLoadingProducts(true)
        const products = await productService.getProducts({
          spids,
          contentfulProductIds,
          useLite: true,
        })

        if (isB2BSelfService) {
          const contentfulProductIdToProductMap: Record<string, InfluencerCardProductDetails> = {}
          products.forEach(product => {
            const selectedOptions = getInitialSelectedOptions(
              product,
              [],
              true,
              false,
              isNestedProduct(product) ? 2 : 1,
            )
            contentfulProductIdToProductMap[product.contentfulProductId] = {
              product,
              selectedOptions,
              productTags: product.tags,
            }
          })
          setContentfulProductIdToProductMap(contentfulProductIdToProductMap)
        } else {
          const spidToProductMap: Record<number, InfluencerCardProductDetails> = {}
          products.forEach(product => {
            const selectedOptions = getInitialSelectedOptions(
              product,
              [],
              true,
              false,
              isNestedProduct(product) ? 2 : 1,
            )
            spidToProductMap[product.spid] = {
              product,
              selectedOptions,
              productTags: product.tags,
            }
          })

          setSpidToProductMap(spidToProductMap)
        }
        setProducts(products)
      } catch (error) {
        captureFatalException(FATAL_EXCEPTION_MESSAGE, {
          error: JSON.stringify(error),
        })
      } finally {
        setIsLoadingProducts(false)
      }
    })()
  }, [influencerDetailList, isB2BSelfService])

  return {
    isLoadingProducts,
    products,
    spidToProductMap,
    contentfulProductIdToProductMap,
  }
}

export { useInfluencerCardProducts }
